/**
 * Create user tab: the Vault create fields (per AI2, plus license type), with
 * picklists from the live vault. Submitting creates a **proposal** — the card
 * below asks for Confirm (and, for an admin-level profile, sends it for
 * Administrator approval). Nothing is written to Veeva on submit.
 */

import { useState } from "react";
import { Alert, Button, Select, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  fieldLabel,
  proposeChange,
  type ChangeOut,
  type PicklistsOut,
} from "@/shared/api/userAdmin";
import { toastApiError } from "@/shared/ui/toast";
import { picklistFor } from "./picks";
import { ProposalCard } from "./ProposalCard";

const FALLBACK_FIELDS = [
  "user_name__v",
  "user_first_name__v",
  "user_last_name__v",
  "user_email__v",
  "user_timezone__v",
  "user_locale__v",
  "user_language__v",
  "security_policy_id__v",
  "security_profile__v",
  "license_type__v",
];

export function CreateUserForm({
  connectionId,
  lists,
}: {
  connectionId: string;
  lists: PicklistsOut | null;
}) {
  const getToken = useAccessToken();
  const fields = lists?.create_fields ?? FALLBACK_FIELDS;
  const required = new Set(lists?.create_required ?? FALLBACK_FIELDS.slice(0, 9));
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<ChangeOut | null>(null);

  const missing = fields.filter((f) => required.has(f) && !values[f]?.trim());
  const set = (f: string, v: string | null) => setValues((prev) => ({ ...prev, [f]: v ?? "" }));

  const submit = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ""));
      setProposal(
        await proposeChange(await getToken(), {
          connection_id: connectionId,
          kind: "create",
          values: clean,
        }),
      );
    } catch (err) {
      toastApiError("Could not prepare the user", err);
    } finally {
      setBusy(false);
    }
  };

  if (proposal) {
    return (
      <Stack gap="sm">
        <ProposalCard initial={proposal} changeId={proposal.change_id} />
        <Button
          variant="default"
          onClick={() => {
            setProposal(null);
            setValues({});
          }}
          style={{ alignSelf: "flex-start" }}
        >
          Prepare another user
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Fill in the new user. You review and confirm before anything is created in Veeva; an
        admin-level security profile also needs an administrator's approval.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {fields.map((f) => {
          const data = picklistFor(lists, f);
          const common = {
            label: fieldLabel(f),
            description: f,
            required: required.has(f),
          };
          return data ? (
            <Select
              key={f}
              {...common}
              data={data}
              value={values[f] || null}
              onChange={(v) => set(f, v)}
              searchable
              clearable={!required.has(f)}
            />
          ) : (
            <TextInput
              key={f}
              {...common}
              value={values[f] ?? ""}
              onChange={(e) => set(f, e.currentTarget.value)}
            />
          );
        })}
      </SimpleGrid>
      {lists && !lists.live.security_profiles && (
        <Alert color="yellow" variant="light">
          This vault did not publish its security profiles; type the profile name (for example
          document_user__v).
        </Alert>
      )}
      <Button
        onClick={() => void submit()}
        loading={busy}
        disabled={missing.length > 0}
        style={{ alignSelf: "flex-start" }}
        data-testid="create-user-submit"
      >
        Review the new user
      </Button>
    </Stack>
  );
}
